import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Lock, Loader2, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { language } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [email, setEmail] = useState('');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setTokenError(language === 'es' ? 'Token no proporcionado' : 'Token not provided');
      return;
    }

    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API}/auth/verify-reset-token?token=${token}`);
      
      if (response.ok) {
        const data = await response.json();
        setValidToken(true);
        setEmail(data.email);
      } else {
        const error = await response.json();
        setTokenError(error.detail || (language === 'es' ? 'Token inválido' : 'Invalid token'));
      }
    } catch (error) {
      console.error('Error verifying token:', error);
      setTokenError(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error(language === 'es' ? 'La contraseña debe tener al menos 6 caracteres' : 'Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error(language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });

      if (response.ok) {
        setSuccess(true);
        toast.success(language === 'es' ? 'Contraseña actualizada' : 'Password updated');
      } else {
        const error = await response.json();
        toast.error(error.detail || (language === 'es' ? 'Error al actualizar la contraseña' : 'Error updating password'));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream via-white to-cream flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-cream flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/logo.svg" alt="MealPlanner" className="w-10 h-10" />
            <span className="font-manrope font-bold text-2xl text-charcoal">MealPlanner</span>
          </Link>
        </div>

        <Card className="bg-white rounded-3xl shadow-soft p-8">
          {/* Token Error State */}
          {!validToken && !loading && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" strokeWidth={1.5} />
              </div>
              <h2 className="font-manrope font-bold text-2xl text-charcoal mb-2">
                {language === 'es' ? 'Enlace no válido' : 'Invalid link'}
              </h2>
              <p className="font-inter text-stone mb-6">
                {tokenError}
              </p>
              <Link to="/forgot-password">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl px-6">
                  {language === 'es' ? 'Solicitar nuevo enlace' : 'Request new link'}
                </Button>
              </Link>
            </div>
          )}

          {/* Success State */}
          {success && (
            <div className="text-center">
              <div className="w-16 h-16 bg-sage/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-sage" strokeWidth={1.5} />
              </div>
              <h2 className="font-manrope font-bold text-2xl text-charcoal mb-2">
                {language === 'es' ? '¡Contraseña actualizada!' : 'Password updated!'}
              </h2>
              <p className="font-inter text-stone mb-6">
                {language === 'es' 
                  ? 'Tu contraseña ha sido cambiada correctamente. Ya puedes iniciar sesión.'
                  : 'Your password has been changed successfully. You can now log in.'}
              </p>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl px-6"
              >
                {language === 'es' ? 'Ir a iniciar sesión' : 'Go to login'}
              </Button>
            </div>
          )}

          {/* Reset Form */}
          {validToken && !success && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h1 className="font-manrope font-bold text-2xl text-charcoal mb-2">
                  {language === 'es' ? 'Nueva contraseña' : 'New password'}
                </h1>
                <p className="font-inter text-stone">
                  {language === 'es' 
                    ? `Introduce una nueva contraseña para ${email}`
                    : `Enter a new password for ${email}`}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="font-manrope text-charcoal">
                    {language === 'es' ? 'Nueva contraseña' : 'New password'}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone" strokeWidth={1.5} />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12 rounded-xl border-border bg-white/50 font-inter"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone hover:text-charcoal"
                      data-testid="reset-password-toggle"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" strokeWidth={1.5} /> : <Eye className="w-5 h-5" strokeWidth={1.5} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="font-manrope text-charcoal">
                    {language === 'es' ? 'Confirmar contraseña' : 'Confirm password'}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone" strokeWidth={1.5} />
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12 rounded-xl border-border bg-white/50 font-inter"
                      data-testid="reset-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone hover:text-charcoal"
                      data-testid="reset-confirm-password-toggle"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" strokeWidth={1.5} /> : <Eye className="w-5 h-5" strokeWidth={1.5} />}
                    </button>
                  </div>
                </div>

                <p className="text-xs text-stone font-inter">
                  {language === 'es' ? 'Mínimo 6 caracteres' : 'Minimum 6 characters'}
                </p>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl h-12 font-manrope font-semibold"
                  data-testid="reset-submit-button"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    language === 'es' ? 'Cambiar contraseña' : 'Change password'
                  )}
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
